/* eslint-disable @typescript-eslint/no-explicit-any */
import {AfterViewInit, ChangeDetectorRef, Component, DestroyRef, DoCheck, inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule, NgForm} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {ConstructionTool} from '../../../../common/models/construction-tool.model';
import {ConstructionToolServiceFactory} from './construction-tool-service.factory';
import {ConstructionToolModelService} from './services/construction-tool-model.service';
import {MapToolsService} from '../../../services/map-tools.service';
import {MapModeService} from '../../../services/map-mode.service';
import {LookupService} from '../../../../common/service/lookup.service';
import {FeatureAttributeConfig} from '../../../../common/models/feature-attributes.model';
import {PlanModelService} from '../../../../common/service/plan-model.service';
import {UserPreferencesService} from '../../../../common/service/user-preferences.service';
import {TraitsService} from '../../../../common/service/traits.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {delay, of, tap} from 'rxjs';
import {MapService} from '../../../services/map.service';
import {filter, switchMap} from 'rxjs/operators';
import {NlrFormFieldHasErrorPipe} from '../../../../common/pipe/nlr-form-field-has-error.pipe';
import {NlrFocusDirective} from '../../../../common/directive/nlr-focus.directive';
import {PermissionsService} from '../../../../common/service/permissions.service';
import {MaxLengthInputLimitDirective} from '../../../../common/directive/max-length-input-limit.directive';
import {NlrAnglePatternDirective} from '../../../../common/directive/nlr-angle-pattern.directive';
import {forkJoinDefaultIfEmpty} from '../../../../common/observable.utils';
import {WidgetWindow, WindowWidgetsService} from '../../../window-widgets/window-widgets.service';
import {equals} from '../../../../common/service/data-util';
import {ToolServiceInterface} from './services/tool-service-interface';
import {DualModeLifecycleManager} from '../dual-mode-lifecycle-manager.service';

@Component({
    selector: 'construction-tool',
    imports: [
        CommonModule,
        NlrFormFieldHasErrorPipe,
        FormsModule,
        NlrFocusDirective,
        MaxLengthInputLimitDirective,
        NlrAnglePatternDirective,
        NgSelectModule
    ],
    providers: [DualModeLifecycleManager],
    templateUrl: './construction-tool.component.html',
    styleUrl: './construction-tool.component.scss'
})
export class ConstructionToolComponent implements OnInit, OnDestroy, DoCheck, AfterViewInit {
    @Input()
    tool: ConstructionTool;
    @Input()
    toolsId: string;

    @ViewChild('constructionToolForm')
    constructionToolForm: NgForm;

    constructionToolService: ToolServiceInterface;

    preserveAttributes: boolean;

    initialised = false;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private constructionToolModelService: ConstructionToolModelService,
        private constructionToolServiceFactory: ConstructionToolServiceFactory,
        private lookupService: LookupService,
        private mapModeService: MapModeService,
        private mapToolsService: MapToolsService,
        private mapService: MapService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private traitsService: TraitsService,
        private userPreferencesService: UserPreferencesService,
        private windowWidgetsService: WindowWidgetsService,
        private lifecycle: DualModeLifecycleManager
    ) {}

    ngOnInit() {
        console.log(`${this.getTitle()} - ngOnInit ${this.getToolType()}`);

        const subCreate = this.eventBus
            .on(`constructionTool:${this.tool.type}:create`)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    console.debug(`${this.getTitle()} - on CreateConstructionTool`);
                    this.constructionToolForm.onSubmit(undefined);
                    this.constructionToolModelService.setSubmitted(this.getToolType(), true);
                }),
                filter(meta => this.toolsId === meta.data && this.constructionToolForm.valid),
                switchMap(() => this.constructionToolService.create(this.getAttributes()))
            )
            .subscribe();

        this.lifecycle.track(subCreate);

        const subClear = this.eventBus
            .on(`constructionTool:${this.tool.type}:clear`)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => {
                    console.debug(`${this.getTitle()} - on ClearConstructionTool`);
                    this.preserveAttributes = false;

                    this.eventBus.cast('selectionMethodChange');
                    return this.setupFeatureAttributes();
                })
            )
            .subscribe();

        this.lifecycle.track(subClear);

        const subReset = this.eventBus
            .on(`constructionTool:${this.tool.type}:reset`)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => {
                    this.eventBus.cast('selectionMethodChange');
                    return this.setupFeatureAttributes();
                }),
                tap(() => {
                    const attrMap = this.getToolAttrs()
                        .filter(attribute => Object.hasOwn(attribute, 'attributeType') && attribute.value)
                        .reduce((acc, attribute) => {
                            acc[attribute.prop] = attribute;
                            return acc;
                        }, {});

                    this.getToolAttrs()
                        .filter(attribute => Object.hasOwn(attribute, 'attributeType') && attrMap[attribute.prop])
                        .forEach(attribute => (attribute.value = attrMap[attribute.prop].value));
                })
            )
            .subscribe();

        this.lifecycle.track(subReset);

        const subTraits = this.eventBus
            .on('traitsChanged')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => this.setupFeatureAttributes())
            )
            .subscribe();

        this.lifecycle.track(subTraits);

        this.constructionToolService = this.constructionToolServiceFactory.getConstructionToolService(this.tool.type);

        this.constructionToolModelService.setTool(this.tool);
    }

    ngAfterViewInit() {
        if (this.getToolAttrs().some(a => !a.prop)) {
            this.getToolAttrs().length = 0;
        }
        (this.getToolAttrs().length === 0 ? this.setupFeatureAttributes() : of(null)).subscribe(() => {
            this.setupFeatureEventModification();
            if (this.constructionToolModelService.createActive) {
                this.constructionToolService.attributes = this.getAttributes();
            }
            this.eventBus.cast('resizeConstructionToolBody');
            if (this.constructionToolModelService.getSubmitted(this.getToolType())) {
                this.constructionToolForm.onSubmit(undefined);
            }

            const parentAttributeNames = this.getToolAttrs()
                .filter(attribute => attribute.parentAttribute)
                .map(attribute => attribute.parentAttribute)
                .filter((v, i, a) => i === a.indexOf(v));

            this.getToolAttrs()
                .filter(attribute => parentAttributeNames.includes(attribute.prop))
                .forEach(attribute => this.attributeSelectorChanged(attribute));

            // Trigger another change detection cycle to prevent "NG0100: Expression Changed After Checked" error
            this.changeDetectorRef.detectChanges();

            this.initialised = true;
        });
    }

    ngOnDestroy() {
        console.log(`${this.getTitle()} - ngOnDestroy ${this.getToolType()}`);
        if (this.mapToolsService.isInactive('CONSTRUCTION_TOOLS_WINDOW')) {
            // Window is already inactive
            this.getToolAttrs().length = 0;
            this.constructionToolModelService.clearAllErrorsAndWarnings();
            return;
        }

        if (!(window as WidgetWindow).nlrpContext && this.windowWidgetsService.isOpen('CONSTRUCTION_TOOLS_WINDOW')) {
            // Window has been undocked so don't deactivate tools
            return;
        }
        this.mapToolsService.deactivate('CONSTRUCTION_TOOLS');
    }

    private typeAttributeValueAtLastCheck: any;

    private toolAttrsAtLastCheck = [];

    ngDoCheck() {
        if (!this.constructionToolForm || !this.initialised) {
            return;
        }

        if (!equals(this.getToolAttrs(), this.toolAttrsAtLastCheck)) {
            const toolAttrs = this.getToolAttrs();
            const typeAttribute = toolAttrs.find(a => a?.prop === 'type');
            if (!typeAttribute) {
                const typeAttributeAtLastCheck = this.toolAttrsAtLastCheck.find(a => a.prop === 'type');
                if (typeAttributeAtLastCheck) {
                    toolAttrs.splice(0, toolAttrs.length, typeAttributeAtLastCheck, ...toolAttrs.filter(a => a));
                }
            }

            this.toolAttrsAtLastCheck = this.windowWidgetsService.structuredClone(this.getToolAttrs());
        }

        const typeAttribute = this.getToolAttrs().find(a => a.prop === 'type');

        const newTypeAttributeValue = typeAttribute && typeof typeAttribute.value === 'object' ? typeAttribute.value.id : typeAttribute?.value;
        if (newTypeAttributeValue !== this.typeAttributeValueAtLastCheck) {
            this.typeAttributeValueAtLastCheck = newTypeAttributeValue;
            this.attributeSelectorChanged(typeAttribute);
        }
    }

    private setupFeatureEventModification() {
        const windowContext = (window as WidgetWindow).nlrpContext;

        if (this.constructionToolService && this.constructionToolService.setupFeatureEventModification) {
            this.constructionToolService.setupFeatureEventModification({
                attributes: this.getToolAttrs(),
                changeDetectorRef: windowContext?.childChangeDetectorRef || this.changeDetectorRef,
                eventBus: this.eventBus
            });
            this.eventBus
                .on('featureHighlightsChanged')
                .pipe(
                    delay(250),
                    tap(() => {
                        console.debug('on featureHighlightChanged construction tool');
                    })
                )
                .subscribe(() => {
                    windowContext?.childChangeDetectorRef?.detectChanges();
                    this.changeDetectorRef.detectChanges();
                });
        }
    }

    private getTitle() {
        return `ConstructionToolComponent for ${this.tool.type}`;
    }

    private getFeatureAttributeConfigs(): FeatureAttributeConfig[] {
        return this.lookupService.getLookups('featureAttributes');
    }

    setupFeatureAttributes(typeAttribute?: any) {
        console.debug(`${this.getTitle()} - setupFeatureAttributes`);

        const priorAttributes = this.getToolAttrs().reduce((acc: any, attribute) => {
            acc[attribute.prop] = attribute;
            return acc;
        }, {});

        this.getToolAttrs().length = 0;

        const featureAttributeConfigs: FeatureAttributeConfig[] = this.getFeatureAttributeConfigs();

        const observables = featureAttributeConfigs.map(attribute => {
            const toolType = this.getToolType();
            const tool = this.getTool();

            if ((attribute.featureType !== toolType && toolType !== 'controlPoint') || !attribute.editableOrderToolsWidget) {
                return of(null);
            }

            if (toolType === 'totalBoundaryDistance' && attribute.prop === 'type') {
                return of(null);
            }

            if (toolType === 'controlPoint' && attribute.featureType !== 'point') {
                return of(null);
            }

            let observable: any = of(null);

            if (attribute.attributeType === 'DOMAIN') {
                const lookupServiceCallback = (lookupValues: any[]) => {
                    const results = lookupValues
                        .filter(l => Object.hasOwn(l, 'planTraitCriteria'))
                        .map(l => this.traitsService.criteriaMatches(l.planTraitCriteria, true));
                    if (results.length) {
                        // filter out lookup values which do not have the required plan trait criteria
                        const filteredLookups = lookupValues.filter(lookupValue => results[lookupValues.indexOf(lookupValue)]);
                        this.populateAttributeValues(attribute, priorAttributes, filteredLookups);
                    } else {
                        // does not contain plan trait criteria - set the attribute details using an unfiltered list
                        this.populateAttributeValues(attribute, priorAttributes, lookupValues);
                    }
                };

                if (attribute.lookupService) {
                    if (toolType === 'controlPoint' && attribute.lookupService === 'pointTypeLookups') {
                        attribute.lookupService = 'controlPointTypeLookups';
                    }

                    let data = this.lookupService.getLookups(attribute.lookupService);
                    if (attribute.featureSubTypeFilter) {
                        data = data.filter((r: any) => tool.featureSubTypeIds.split(',').includes(r.id.toString()));
                    }
                    lookupServiceCallback(data);
                } else if (attribute.customService) {
                    observable = this.mapService
                        .getCustomLookups(attribute.customService + '/' + this.planModelService.getGuid(), attribute)
                        .pipe(tap(lookupServiceCallback));
                }
            } else if (attribute.attributeType.indexOf('RADIO') !== -1) {
                if (attribute.prop !== 'creationmethod' || !priorAttributes[attribute.prop]) {
                    if (attribute.featureType === 'mergeLines' && attribute.prop === 'distanceCalculationMethod') {
                        attribute.value =
                            this.userPreferencesService.userPreferences?.mergeLinesDistanceCalculationMethod === 'SUM_SELECTED_LINES' ? 'YES' : 'NO';
                    } else {
                        attribute.value = attribute.lookupDefaultId;
                    }
                }

                if (!attribute.value && priorAttributes[attribute.prop] && priorAttributes[attribute.prop].value) {
                    attribute.value = priorAttributes[attribute.prop].value;
                }

                if (attribute.labels) {
                    let items = attribute.labels.split(',');

                    if (items.length !== 2) {
                        items = [];
                        items[0] = 'Yes';
                        items[1] = 'No';
                    }

                    attribute.values = [
                        {value: 'YES', description: items[0]},
                        {value: 'NO', description: items[1]}
                    ];
                } else {
                    attribute.values = [
                        {value: 'YES', description: 'Yes'},
                        {value: 'NO', description: 'No'}
                    ];
                }
            } else if (attribute.attributeType.indexOf('BOOLEAN') !== -1) {
                const attributeValue = String(attribute.lookupDefaultId).toLowerCase();

                if (attributeValue === 't' || attributeValue === 'true' || attributeValue === 'y' || attributeValue === 'yes') {
                    attribute.value = true;
                } else if (attributeValue === 'f' || attributeValue === 'false' || attributeValue === 'n' || attributeValue === 'no') {
                    attribute.value = false;
                } else if (priorAttributes[attribute.prop] && priorAttributes[attribute.prop].value) {
                    attribute.value = priorAttributes[attribute.prop].value;
                }
            } else if (attribute.attributeType.indexOf('STRING') !== -1) {
                attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);

                if (attribute.editable) {
                    if (attribute.lookupDefaultId) {
                        //Set attribute value to default lookup value
                        attribute.value = attribute.lookupDefaultId;
                    } else if (typeof attribute.value !== 'undefined') {
                        //No default lookup value, so delete the value.
                        delete attribute.value;
                    }
                } else {
                    if (priorAttributes[attribute.prop] && typeof priorAttributes[attribute.prop].value !== 'undefined') {
                        //Reset the readonly value
                        attribute.value = priorAttributes[attribute.prop].value;
                    }
                }
            } else if (attribute.attributeType.indexOf('DECIMAL') !== -1) {
                const decimalLength = this.getAttributeTypeLength(attribute.attributeType);

                if (!attribute.pattern) {
                    attribute.pattern = `^-?[0-9]+(.[0-9]{1,${decimalLength}})?$`;
                }

                if (attribute.editable) {
                    if (!isNaN(parseFloat(attribute.lookupDefaultId))) {
                        // Set attribute value to default lookup value
                        attribute.value = parseFloat(attribute.lookupDefaultId);
                    } else if (attribute.featureType === 'dissectLine' && this.preserveAttributes) {
                        // Allows editable field 'Dissection distance' to maintain it value
                        if (
                            attribute.prop === 'distance' &&
                            priorAttributes[attribute.prop] &&
                            typeof priorAttributes[attribute.prop].value !== 'undefined'
                        ) {
                            // Reset the readonly value
                            attribute.value = priorAttributes[attribute.prop].value;
                        }
                    } else if (attribute.featureType === 'mergeLines') {
                        if (!Object.hasOwn(attribute, 'value')) {
                            attribute.value = undefined;
                        }
                        // allow the distance attribute to maintain its value
                        if (attribute.prop === 'distance') {
                            if (priorAttributes[attribute.prop] && typeof priorAttributes[attribute.prop].value !== 'undefined') {
                                attribute.value = priorAttributes[attribute.prop].value;
                            }
                        }
                    } else if (typeof attribute.value !== 'undefined') {
                        //No default lookup value, so delete the value.
                        delete attribute.value;
                    }
                } else {
                    if (priorAttributes[attribute.prop] && typeof priorAttributes[attribute.prop].value !== 'undefined') {
                        //Reset the readonly value
                        attribute.value = priorAttributes[attribute.prop].value;
                    }
                }
            } else if (attribute.attributeType.includes('INTEGER')) {
                attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);
                attribute.min = 1;
                attribute.max = Math.min(Math.pow(10, attribute.attributeTypeLength) - 1, Number.MAX_SAFE_INTEGER);
                attribute.pattern = '[0-9]+';

                if (attribute.editable) {
                    if (!isNaN(parseInt(attribute.lookupDefaultId))) {
                        //Set attribute value to default lookup value
                        attribute.value = parseInt(attribute.lookupDefaultId);
                    } else if (attribute.featureType === 'lot' && this.preserveAttributes) {
                        //Allows editable field 'Lot Number' to maintain it value
                        if (
                            attribute.prop === 'name' &&
                            priorAttributes[attribute.prop] &&
                            typeof priorAttributes[attribute.prop].value !== 'undefined'
                        ) {
                            attribute.value = priorAttributes[attribute.prop].value;
                        }
                    } else if (typeof attribute.value !== 'undefined') {
                        //No default lookup value, so delete the value.
                        delete attribute.value;
                    }
                } else {
                    if (priorAttributes[attribute.prop] && typeof priorAttributes[attribute.prop].value !== 'undefined') {
                        //Reset the readonly value
                        attribute.value = priorAttributes[attribute.prop].value;
                    }
                }
            }

            if (attribute.prop === 'acquisitiontransferaction' && (!attribute.values || !attribute.values.length)) {
                return observable;
            }

            return observable.pipe(
                tap(() => {
                    this.getToolAttrs().push(attribute);
                    this.preserveAttributes = true;
                })
            );
        });

        return forkJoinDefaultIfEmpty(observables).pipe(
            tap(() => {
                const toolAttrs = this.getToolAttrs();

                toolAttrs.push({prop: 'guid', value: this.planModelService.getGuid()});

                typeAttribute = typeAttribute || toolAttrs.find(attr => attr.prop === 'type');

                if (typeAttribute) {
                    const validSubTypeIds = this.spatialUtilService.parseIdsAttr(typeAttribute.validSubTypeIds);

                    if (typeAttribute.values && validSubTypeIds.length) {
                        const filteredTypeAttributeValues = typeAttribute.values.filter((v: any) => validSubTypeIds.includes(v.id));
                        typeAttribute.values.splice(0, typeAttribute.values.length, ...filteredTypeAttributeValues);
                    }

                    if (typeAttribute.value) {
                        toolAttrs.splice(
                            0,
                            toolAttrs.length,
                            ...toolAttrs.filter(attr => {
                                const attrValidSubTypeIds = this.spatialUtilService.parseIdsAttr(attr.validSubTypeIds);
                                return attrValidSubTypeIds.length ? attrValidSubTypeIds.includes(typeAttribute.value.id) : true;
                            })
                        );

                        toolAttrs
                            .filter(a => this.spatialUtilService.parseIdsAttr(a.subTypesMandatoryAtConstruct).includes(typeAttribute.value.id))
                            .forEach(a => (a.mandatory = true));
                    }
                } else {
                    toolAttrs
                        .filter(
                            a =>
                                a.subTypesMandatoryAtConstruct === 'TRUE' ||
                                (a.validSubTypeIds && a.validSubTypeIds === a.subTypesMandatoryAtConstruct)
                        )
                        .forEach(a => (a.mandatory = true));
                }
            })
        );
    }

    private getAttributes() {
        return this.getToolAttrs().reduce((acc: any, attribute) => {
            if (attribute.parentAttribute === null || this.isAttributeUnique(attribute)) {
                acc[attribute.prop] = attribute.value;
            } else if (attribute.showBasedOnParent) {
                acc[attribute.prop] = {id: `[${this.getParentAttribute(attribute.parentAttribute).value.id}]-${attribute.value}`};
            }
            return acc;
        }, {});
    }

    private populateAttributeValues(attribute: FeatureAttributeConfig, priorAttributes: any, lookupValues: any[]) {
        // filter out values with display value of "false"
        attribute.values = lookupValues.filter((item: any) => !Object.hasOwn(item, 'display') || item.display === true);

        if (this.getToolType() === 'totalBoundaryDistance') {
            const surveyMethod = this.planModelService.getModel().surveyMethod.planSurveyMethod.toString().includes('UNSURVEYED')
                ? 'UNSURVEYED'
                : 'SURVEYED';
            attribute.values.forEach((value: any) => {
                if (surveyMethod.localeCompare(value.description, undefined, {sensitivity: 'accent'}) === 0) {
                    attribute.value = value;
                }
            });
            return;
        }

        if (attribute.prop === 'acquisitiontransferaction') {
            const parcelTypeAttr = priorAttributes['type'];

            if (parcelTypeAttr && parcelTypeAttr.featureType === 'lot') {
                attribute.values = lookupValues.filter(l => l.validParcelTypeId === parcelTypeAttr.value.id);
                attribute.value = attribute.values[0];
            } else {
                // can't filter by parcel type as it hasn't been selected yet - manually remove duplicates
                attribute.values = attribute.values.filter(
                    (v: any, i: number, a: any[]) => i === a.findIndex(value => value.description === v.description)
                );
                attribute.value = attribute.values[0];
            }
        }

        if (attribute.lookupDefaultId) {
            attribute.values.forEach((value: any) => {
                if (`${value.id}` === attribute.lookupDefaultId) {
                    attribute.value = value;
                }
            });
            if (typeof attribute.value === 'undefined' && attribute.values?.length > 0) {
                //default not found in the list, set the first one
                attribute.value = attribute.values[0];
            }
        } else if (priorAttributes[attribute.prop] && priorAttributes[attribute.prop].value && priorAttributes[attribute.prop].value.id) {
            attribute.values.forEach((value: any) => {
                if (`${value.id}` === priorAttributes[attribute.prop].value.id) {
                    attribute.value = value;
                }
            });
        }
    }

    getAttributeTypeLength(attributeType: string) {
        console.debug(`${this.getTitle()} - getAttributeTypeLength`);

        const lengthStartIndex = attributeType.indexOf('(') + 1;
        const lengthEndIndex = attributeType.indexOf(')');
        return parseInt(attributeType.substring(lengthStartIndex, lengthEndIndex));
    }

    checkChildrenAttributesDisplay(attribute: FeatureAttributeConfig) {
        console.debug(`${this.getTitle()} - checkChildrenAttributesDisplay`);
        if (!attribute?.prop) {
            return;
        }

        this.getToolAttrs()
            .filter((a: any) => a && a.parentAttribute !== null && a.parentAttribute === attribute.prop)
            .forEach((a: any) => (a.showBasedOnParent = this.showAttributeBasedOnValidParentLookupIds(a)));
    }

    showAttributeBasedOnValidParentLookupIds(attribute: FeatureAttributeConfig) {
        console.debug(`${this.getTitle()} - showAttributeBasedOnValidParentLookupIds`);

        let showAttribute = false;

        const parentAttribute = this.getParentAttribute(attribute.parentAttribute);

        if (parentAttribute) {
            const validParentLookupIds = attribute.validParentLookupIds.split(',');

            const validParentLookupId = validParentLookupIds.filter((parentLookupId: any) => {
                return parentLookupId === parentAttribute.value || parentLookupId === parentAttribute.value.id;
            });

            if (validParentLookupId?.length > 0) {
                showAttribute = true;
            }
        }

        return showAttribute;
    }

    getParentAttribute(property: string) {
        console.debug(`${this.getTitle()} - getParentAttribute`);

        const parentAttribute = this.getToolAttrs().find(a => a.prop === property);

        return parentAttribute && parentAttribute.value ? parentAttribute : undefined;
    }

    checkReadOnlyAttributeBasedOnValidParentLookupIds(attribute: FeatureAttributeConfig) {
        console.debug(`${this.getTitle()} - checkReadOnlyAttributeBasedOnValidParentLookupIds`);

        if (attribute?.value && attribute?.parentAttribute === null) {
            this.getToolAttrs()
                .filter(a => a.parentAttribute !== null && a.parentAttribute === attribute.prop)
                .forEach(a => {
                    if (a.parentLookupIdsEditable === null) {
                        a.readyOnlyBasedOnParent = false;
                    } else {
                        a.readyOnlyBasedOnParent = !a.parentLookupIdsEditable.split(',').some((id: string) => id === attribute.value.id);
                    }
                });
        }
    }

    populateAttributeBasedOnValidParentLookupIds(attribute: FeatureAttributeConfig) {
        console.debug(`${this.getTitle()} - populateAttributeBasedOnValidParentLookupIds`);

        if (attribute?.value) {
            this.getToolAttrs()
                .filter(a => a.parentAttribute !== null && a.parentLookupProperty !== null && a.parentAttribute === attribute.prop)
                .forEach(a => (a.value = attribute.value[a.parentLookupProperty]));
        }
    }

    isAttributeUnique(attribute: FeatureAttributeConfig) {
        console.debug(`${this.getTitle()} - isAttributeUnique`);

        return this.getToolAttrs().filter(a => a.prop === attribute.prop).length === 1;
    }

    showAttribute(attrName: string) {
        if (this.constructionToolService?.showAttribute) {
            return this.constructionToolService.showAttribute(attrName);
        }
        return true;
    }

    isFieldDisabled(attribute: FeatureAttributeConfig) {
        if (!this.permissionsService.hasEditPermission(this.tool.permissions)) {
            return true;
        }

        return !attribute.editable || !this.mapModeService.isMapModeType('mainPlan');
    }

    getVisibleToolAttrs(): FeatureAttributeConfig[] {
        if (!this.permissionsService.hasPermission(this.tool.permissions)) {
            return [];
        }

        return this.getToolAttrs()
            .filter((attribute: any) => attribute && this.showAttribute(attribute.prop))
            .sort((a: any, b: any) => {
                if (a.editableOrderToolsWidget && b.editableOrderToolsWidget) {
                    return a.editableOrderToolsWidget - b.editableOrderToolsWidget;
                } else if (a.editableOrderToolsWidget) {
                    return -1;
                } else if (b.editableOrderToolsWidget) {
                    return 1;
                }
                return 0;
            });
    }

    private getToolAttrs(): FeatureAttributeConfig[] {
        return this.constructionToolModelService.getToolAttrs(this.tool.type);
    }

    private getTool() {
        return this.constructionToolModelService.getTool(this.tool.type);
    }

    private getToolType() {
        return this.tool.type;
    }

    attributeSelectorChanged(selectedAttribute: any) {
        console.debug(`${this.getTitle()} - attributeSelectorChanged`);

        if (!selectedAttribute) {
            return;
        }

        console.debug(`${this.getTitle()} - attributeSelectorChanged, selectedAttribute:`, selectedAttribute);

        if (selectedAttribute.prop === 'type') {
            if (this.constructionToolForm.submitted) {
                //TODO: this.constructionToolForm - set pristine?
                //this.constructionToolForm.resetForm() clears everything including type?
                this.constructionToolModelService.setSubmitted(this.getToolType(), false);
            }

            const selectedAttributeClone = this.windowWidgetsService.structuredClone(selectedAttribute);
            console.debug(`${this.getTitle()} - attributeSelectorChanged, before setupFeatureAttributes - selectedAttribute:`, selectedAttribute);
            this.setupFeatureAttributes(selectedAttribute).subscribe();
            console.debug(`${this.getTitle()} - attributeSelectorChanged, after setupFeatureAttributes - selectedAttribute:`, selectedAttribute);
            if (selectedAttribute.values?.length && (!selectedAttributeClone.values || selectedAttributeClone.values.length === 0)) {
                selectedAttributeClone.values = selectedAttribute.values.map((v: any) => this.windowWidgetsService.structuredClone(v));
            }
            if (selectedAttribute.value) {
                selectedAttributeClone.value = selectedAttributeClone.values.find((v: any) => v.id === selectedAttribute.value.id);
            }
            selectedAttribute = selectedAttributeClone;

            // Put type attribute back as it has been reset by the setupEditor function
            const toolAttrs = this.getToolAttrs();

            console.debug(`${this.getTitle()} - attributeSelectorChanged, toolAttrs:`, toolAttrs);

            const toolTypeAttrIndex = toolAttrs.findIndex((attribute: any) => attribute.prop === 'type');
            toolAttrs.splice(toolTypeAttrIndex, 1, selectedAttribute);
        }

        console.debug(`${this.getTitle()} - attributeSelectorChanged, selectedAttribute:`, selectedAttribute);

        this.checkChildrenAttributesDisplay(selectedAttribute);
        this.populateAttributeBasedOnValidParentLookupIds(selectedAttribute);
        this.checkReadOnlyAttributeBasedOnValidParentLookupIds(selectedAttribute);
        if (selectedAttribute?.name === 'Type') {
            this.eventBus.cast(`${this.getToolType()}:typeChange`);
            if (this.constructionToolService && this.constructionToolService.onTypeChange) {
                this.constructionToolService.onTypeChange(this.getAttributes());
            }
        }
    }
}
